import React, {useEffect, useState} from 'react';
import {Modal, Button} from 'react-bootstrap';
import DataTable from '../../../../common/DataTable';
import {NOTE_FILE_HEADERS} from '../actions/constant';
import {
  downloadNotesFile,
  downloadZipFileForNotes,
  getCaseNotesPopUpDetails,
} from '../../../../services/CaseActivityAPI';
import UploadStatusFloatingIcon from '../../../../common/UploadStatusFloatingIcon';
import PdfViewerDrawer from '../../../../common/PDFFileViewerDrawer';

interface FileModalProps {
  show: boolean;
  onClose: () => void;
  keyId: number | null;
}

const NotesPopUpModal: React.FC<FileModalProps> = ({show, onClose, keyId}) => {
  const [notesData, setNotesData] = useState<any[]>([]);
  const [category, setCategory] = useState('');
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [downloadStatuses, setDownloadStatuses] = useState<any>({});
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const fetchNotesDetails = async (query: string = '') => {
    const notesDetails = await getCaseNotesPopUpDetails(keyId);
    if (notesDetails) {
      setNotesData(notesDetails);
      setCategory(notesDetails[0].noteCategoryDescription);
      setTotalPages(
        notesDetails[0].totalPageCount > 0
          ? notesDetails[0].totalPageCount
          : null,
      );
    }
  };

  useEffect(() => {
    if (keyId) {
      fetchNotesDetails();
    }
  }, [keyId]);

  const handleDownloadProgress = async (
    id: string,
    downloadFn: () => Promise<any>,
    fileName: string = 'File.zip',
  ) => {
    setDownloadStatuses((prev: any) => ({
      ...prev,
      [id]: {fileName, progress: 0, isUploading: true},
    }));

    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      if (progress >= 90) return;
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: {...prev[id], progress},
      }));
    }, 300);

    try {
      await downloadFn();
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: {...prev[id], progress: 100, isUploading: false},
      }));
      setTimeout(() => {
        setDownloadStatuses((prev: any) => {
          const updated = {...prev};
          delete updated[id];
          return updated;
        });
      }, 2000);
    } catch (error) {
      console.error('Download error:', error);
      clearInterval(interval);
      setDownloadStatuses((prev: any) => ({
        ...prev,
        [id]: {...prev[id], isUploading: false},
      }));
    }
  };

  const handleDownloadAction = (row: any) => {
    handleDownloadProgress(
      row?.caseRecordDetailsId,
      () => downloadNotesFile(row?.attachmentId, row?.fileName),
      row?.fileName,
    );
  };

  const handlePopUpDetailsFileDownloadAction = () => {
    const ids = notesData.map((item) => item.attachmentId).join(',');
    handleDownloadProgress(`popup-${Date.now()}`, () =>
      downloadZipFileForNotes(ids),
    );
  };

  const viewPDF = (row: any) => {
    const fileUrl = row.downloadFile;
    const isPdf = fileUrl?.toLowerCase().endsWith('.pdf');
    const isImage = /\.(png|jpe?g|gif|tiff?|heif|heic|webp)$/i.test(
      fileUrl || '',
    );

    if (isPdf || isImage) {
      setSelectedPdfUrl(fileUrl); // Or better name: setSelectedFileUrl
      setIsViewerOpen(true);
    } else {
      alert('Unsupported file format');
    }
  };

  return (
    <>
      <UploadStatusFloatingIcon
        uploadStatuses={downloadStatuses}
        isUpload={false}
        onCancelUpload={(id) => {
          setDownloadStatuses((prev: any) => {
            const updated = {...prev};
            delete updated[id];
            return updated;
          });
        }}
      />
      {!isViewerOpen ? (
        <Modal
          show={show}
          onHide={onClose}
          size='xl'
          aria-labelledby='contained-modal-title-vcenter'
          centered
          className='recordList'
        >
          <Modal.Header closeButton>
            <Modal.Title className='fw-bold text-left modelheading offcanvas-title d-flex align-items-center'>
              View Attachments {''}
              <span className='text-dark fw-medium mx-2'>
                Category - {category}
              </span>{' '}
              {totalPages != null && (
                <>
                  <span className='ms-3 material-icons theme-color'>
                    auto_stories
                  </span>
                  <span className='ms-1 theme-color'>Pages -</span>{' '}
                  <span className='text-muted'>{totalPages}</span>
                </>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <DataTable
              headers={NOTE_FILE_HEADERS}
              rows={notesData} // Pass the filtered data
              onDownloadAction={handleDownloadAction}
              onViewPdf={viewPDF}
            />
          </Modal.Body>
          <Modal.Footer className='border-top d-flex justify-content-end'>
            <Button
              variant='outline-info'
              onClick={onClose}
              className='d-flex align-items-center btn btn-outline-info'
            >
              <span className='material-icons mr-2'>close</span> Close
            </Button>
            <Button
              variant='primary'
              className='btn btn-primary d-flex align-items-center mx-2 btn btn-primary'
              onClick={handlePopUpDetailsFileDownloadAction}
            >
              <span className='material-icons mx-1'>download</span> Download All
            </Button>
          </Modal.Footer>
        </Modal>
      ) : null}
      <PdfViewerDrawer
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
        fileUrl={selectedPdfUrl}
      />
    </>
  );
};

export default NotesPopUpModal;
